'use client';

import React, { forwardRef } from 'react';
import { cn } from '@/components/ui/_lib/cn';
import type { UiSkeletonProps } from '@/components/ui/_lib/skeletonProps';
import { useDeferredSkeleton } from '@/components/ui/_lib/useDeferredSkeleton';
import { SkeletonLines } from '@/components/ui/_lib/SkeletonLines';
import { SKELETON_BY_COMPONENT } from '@/components/ui/_lib/skeletonRegistry';

/** Standard anchor props (including native `title` for tooltips) plus skeleton UI. */
export type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  'aria-label'?: string;
  disabled?: boolean;
} & UiSkeletonProps;

const sk = SKELETON_BY_COMPONENT.Anchor;

const Anchor = forwardRef<HTMLAnchorElement, AnchorProps>((props, ref) => {
  const {
    skeleton = false,
    skeletonMaxWaitMs = 2000,
    skeletonLines,
    className,
    children,
    ...domProps
  } = props;

  const showSkeleton = useDeferredSkeleton(skeleton, skeletonMaxWaitMs);

  return (
    <a
      ref={ref}
      className={cn('group', 'anchor', className)}
      {...domProps}
    >
      <SkeletonLines show={showSkeleton} shape={sk.shape} lines={skeletonLines ?? sk.lines}>
        {children}
      </SkeletonLines>
    </a>
  );
});

Anchor.displayName = 'Anchor';

export default Anchor;
